/*
 * Copyright (c) 2025 curryfirm.com
 */

// features/dumpsters/dumpstersSlice.js
import * as constants from "../../constants.js";
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import fetchDumpsterInfo from "./fetchDumpsterInfo.js";
import { DROP_LOT_STREETS } from "../../constants.js";


// Thunk to load dumpster info from DumpsterTrack Sheets document.
// Implement assumption that all dumpsters at the drop-lot are empty.
export const fetchDumpstersThunk = createAsyncThunk(
    'dumpsters/fetchDumpsters',
    async (_, thunkAPI) => {
        const { columnMap, rows } = await fetchDumpsterInfo();
        if (!(columnMap && rows && rows.length > 0)) {
            throw new Error("DumpsterInfo did not load");
        }
        return rows
            .map(row => ({
                dumpsterId: row[columnMap[constants.DUMPSTER_INFO_COLUMN_TITLES.dumpsterSerial]],
                capacity: row[columnMap[constants.DUMPSTER_INFO_COLUMN_TITLES.dumpsterCapacity]],
                locationId: row[columnMap[constants.DUMPSTER_INFO_COLUMN_TITLES.locationId]],
                customer: row[columnMap[constants.DUMPSTER_INFO_COLUMN_TITLES.customer]],
                lat: row[columnMap[constants.DUMPSTER_INFO_COLUMN_TITLES.latitude]],
                lng: row[columnMap[constants.DUMPSTER_INFO_COLUMN_TITLES.longitude]],
                street: row[columnMap[constants.DUMPSTER_INFO_COLUMN_TITLES.street]],
                city: row[columnMap[constants.DUMPSTER_INFO_COLUMN_TITLES.city]],
                state: row[columnMap[constants.DUMPSTER_INFO_COLUMN_TITLES.state]],
                zip: row[columnMap[constants.DUMPSTER_INFO_COLUMN_TITLES.zip]],
                tag: "",
                googlePlaceId: "",
                userNote: "",
            }));
    }
);

const dumpstersSlice = createSlice({
    name: 'dumpsters',
    initialState: {
        all: [], // Array of all dumpster objects
        loading: false,
        error: null,
    },
    reducers: {
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDumpstersThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchDumpstersThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.all = action.payload;
            })
            .addCase(fetchDumpstersThunk.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    action.payload?.message || // for thunks that use rejectWithValue
                    action.error?.message ||   // fallback for plain thrown errors
                    action.error ||            // fallback in case only a string is present
                    "Unknown error";
            });

    }
});

export const {
} = dumpstersSlice.actions;

export default dumpstersSlice.reducer;

// Selectors
export const selectAllDumpsters = state => state.dumpsters.all;
export const selectDumpstersAreLoading = state => state.dumpsters.loading;
export const selectDumpsterError = state => state.dumpsters.error;

// TODO: Abstract and improve how we say a dumpster is at a drop-lot.
export const selectDumpstersAtLot = createSelector(
    [selectAllDumpsters],
    allDumpsters => allDumpsters.filter(
        d => DROP_LOT_STREETS.some(
            sought => d.street.toLowerCase().includes(sought.toLowerCase())
        )
    )
);

export const selectDumpstersNotAtLot = createSelector(
    [selectAllDumpsters],
    allDumpsters => allDumpsters.filter(
        d => ! DROP_LOT_STREETS.some(
            sought => d.street.toLowerCase().includes(sought.toLowerCase())
        )
    )
);